import { QueryHookOptions } from "@apollo/client"
import { HStack, Separator, Text } from "@chakra-ui/react"
import PopupAlert from "@pathwright/ui/src/components/alert/PopupAlert"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import Dropdown from "@pathwright/ui/src/components/dropdown"
import SubmitHandler, {
  SUBMIT_STATUS
} from "@pathwright/ui/src/components/submit/SubmitHandler"
import { flattenEdges } from "@pathwright/utils"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import React from "react"
import {
  DiscussionPostFragment,
  DiscussionResponseFragment,
  NotificationAction,
  NotificationTarget,
  RegistrationRoleEnum,
  RoleFilter,
  UserFragment,
  useCreateNotificationMutation,
  useRegistrationsLazyQuery,
  useRegistrationsQuery
} from "@pathwright/graphql-client/src/generated"
import { useDiscussionPerms } from "../../permissions"

type UseAskToRespondProps = {
  discussion: Pick<
    DiscussionPostFragment,
    "id" | "resource_id" | "cohort_id" | "context_id"
  >
  response?: Pick<DiscussionResponseFragment, "id"> | null
}

// Wrapper hook to use the create notification mutation.
export function useAskToRespond(props?: UseAskToRespondProps) {
  const [createNotification, mutationState] = useCreateNotificationMutation()

  const askToRespond = (
    user: UserFragment,
    overrideProps?: UseAskToRespondProps
  ) => {
    const effectiveProps = overrideProps || props!

    let notificationAction = NotificationAction.DiscussionAskToAnswer
    let notificationTarget = NotificationTarget.Discussion
    let notificationTargetId = effectiveProps.discussion.id
    // If scoping to a response, adjust target and id.
    if ("response" in effectiveProps && effectiveProps.response) {
      notificationAction = NotificationAction.ResponseAskToReply
      notificationTarget = NotificationTarget.Response
      notificationTargetId = effectiveProps.response.id
    }

    return createNotification({
      variables: {
        input: {
          action: notificationAction,
          target: notificationTarget,
          target_id: notificationTargetId,
          to_users: [user.id],
          context: {
            resource_id: effectiveProps.discussion.resource_id,
            cohort_id: effectiveProps.discussion.cohort_id,
            discussion_context_id: effectiveProps.discussion.context_id
          }
        }
      }
    })
  }

  return [askToRespond, mutationState] as const
}

type AskToRespondRegistrationQueryOptions = Partial<
  Exclude<QueryHookOptions<any, any>, "variables">
>

const roleFilter: RoleFilter = {
  gte: RegistrationRoleEnum.Moderator,
  lte: RegistrationRoleEnum.Teacher
}

export function useAskToRespondRegistrations(
  cohortId: number,
  options?: AskToRespondRegistrationQueryOptions
  // options?: QueryHookOptions<any, any>
) {
  const registrationsQuery = useRegistrationsQuery({
    variables: {
      groupId: cohortId,
      roleFilter
    },
    ...options
  })

  return registrationsQuery
}

export function useAskToRespondLazyRegistrations(
  cohortId: number,
  options?: AskToRespondRegistrationQueryOptions
  // options?: QueryHookOptions<any, any>
) {
  const registrationsLazyQuery = useRegistrationsLazyQuery({
    variables: {
      groupId: cohortId,
      roleFilter
    },
    ...options
  })

  return registrationsLazyQuery
}

type AskToRespondProps = {
  // Function prop to render the item with a wrapper.
  children: (props: {
    onClick: () => void
    item: React.ReactNode
  }) => React.ReactNode
} & UseAskToRespondProps

export function AskToRespond(props: AskToRespondProps) {
  const { discussion, children } = props
  const { tc } = useTranslate()
  const registrationsQuery = useAskToRespondRegistrations(discussion.cohort_id!)

  const registrations =
    flattenEdges(registrationsQuery.data?.group?.registrations) || []

  const [askToRespond] = useAskToRespond(props)

  return registrations.length ? (
    <>
      <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
      <Text
        as="span"
        fontSize="xs"
        fontWeight="semibold"
        whiteSpace="nowrap"
        p={2}
      >
        {tc("ask to respond")}
      </Text>
      {registrations.map((registration) => (
        <SubmitHandler
          key={registration.id}
          onSubmit={() => askToRespond(registration.user)}
        >
          {({
            handleSubmit,
            status,
            error,
            clearError
          }: {
            handleSubmit: () => void
            status: any
            error: string | null
            clearError: () => void
          }) => (
            <React.Fragment key={registration.id}>
              {children({
                onClick: handleSubmit,
                item: (
                  <HStack>
                    <UserAvatar user={registration.user} size="2xs" />
                    <Text as="span" fontSize="xs">
                      {registration.user.display_name}
                    </Text>
                  </HStack>
                )
              })}
              {(status === SUBMIT_STATUS.LOADING ||
                status === SUBMIT_STATUS.SUCCESS) && (
                <PopupAlert
                  success={tc("asked {{ user }} to respond.", {
                    user: registration.user.display_name
                  })}
                />
              )}
              {!!error && (
                <PopupAlert
                  error={error}
                  onClear={clearError}
                  clearAfter={null}
                />
              )}
            </React.Fragment>
          )}
        </SubmitHandler>
      ))}
    </>
  ) : null
}

type DiscussionListItemControlsProps = {
  discussion: DiscussionPostFragment
  handleEdit: (discussion: DiscussionPostFragment) => void
  handleDelete: (discussion: DiscussionPostFragment) => void
  handleCopyLink: (discussion: DiscussionPostFragment) => void
  copyingLink: boolean
}

const DiscussionListItemControls = ({
  discussion,
  handleEdit,
  handleDelete,
  handleCopyLink,
  copyingLink
}: DiscussionListItemControlsProps) => {
  const discussionPerms = useDiscussionPerms(discussion)
  const { tc } = useTranslate()

  if (
    !discussionPerms.canEdit &&
    !discussionPerms.canDelete &&
    !discussionPerms.canAskToAnswer
  ) {
    return null
  }

  return (
    <Dropdown.Trigger
      className="DiscussionListItemControls"
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      renderDropdown={() => (
        <Dropdown.Menu>
          {/* <Dropdown.Item
            label={tc(copyingLink ? "link copied!" : "copy link")}
            onClick={() => handleCopyLink(discussion)}
            icon="copy"
          /> */}
          {discussionPerms.canEdit && (
            <Dropdown.Item
              label={tc("edit")}
              onClick={() => handleEdit(discussion)}
              closeOnClick
              icon="edit"
            />
          )}
          {discussionPerms.canDelete && (
            <Dropdown.Item
              label={tc("delete")}
              onClick={() => handleDelete(discussion)}
              closeOnClick
              icon="trash"
            />
          )}
          {discussionPerms.canAskToAnswer && (
            <AskToRespond discussion={discussion}>
              {({ onClick, item }) => (
                <Dropdown.Item onClick={onClick}>{item}</Dropdown.Item>
              )}
            </AskToRespond>
          )}
        </Dropdown.Menu>
      )}
    >
      <IconButton styleType="secondary" icon="ellipses-horizontal" />
    </Dropdown.Trigger>
  )
}

DiscussionListItemControls.displayName = "DiscussionListItemControls"

export default DiscussionListItemControls
